import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useOnClickOutside } from '@hooks';

/* A listbox rather than a native <select>: the native control cannot be styled
   on macOS or Windows, so it would arrive as a piece of system chrome in the
   middle of the page. This keeps the site's own border, type and accent, and
   leaves room for the per-tag counts, which are the useful part — they tell
   you whether a filter is worth applying before you apply it. */
const Wrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  padding: calc(var(--space-2) + var(--optical)) var(--space-4)
    calc(var(--space-2) - var(--optical));
  background-color: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--border-radius);
  color: var(--text-emphasis);
  font-family: var(--font-sans);
  font-size: var(--fz-md);
  line-height: 1;
  transition: var(--transition);

  &:hover,
  &:focus-visible {
    border-color: var(--accent);
    color: var(--text);
  }

  .value {
    color: ${props => (props.isFiltered ? 'var(--accent)' : 'inherit')};
  }

  .chevron {
    width: 10px;
    height: 10px;
    flex: 0 0 10px;
    /* The asymmetric padding above pushes the whole content box down so the
       label's cap band lands on the border-box centre. The chevron is a plain
       box that was already centred, so it rides down with it — this takes it
       back up onto the label. */
    position: relative;
    top: calc(var(--optical) * -1);
    /* Points down when closed, up when open — the only state cue the trigger
       carries besides the label. */
    transform: rotate(${props => (props.isOpen ? '180deg' : '0deg')});
    transition: transform 0.2s var(--easing);

    svg {
      display: block;
      width: 100%;
      height: 100%;
      fill: none;
      stroke: currentColor;
      stroke-width: 2.5;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }
`;

const Panel = styled.ul`
  ${({ theme }) => theme.mixins.resetList};
  position: absolute;
  top: calc(100% + var(--space-2));
  left: 0;
  z-index: 10;
  min-width: 100%;
  max-height: 18rem;
  overflow-y: auto;
  padding: var(--space-2);
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--border-radius);
  box-shadow: 0 12px 30px -12px var(--shadow);

  /* Fades and lifts rather than appearing, matching the chat panel's entrance. */
  opacity: ${props => (props.isOpen ? 1 : 0)};
  visibility: ${props => (props.isOpen ? 'visible' : 'hidden')};
  transform: translateY(${props => (props.isOpen ? '0' : '-6px')});
  transition: opacity 0.18s var(--easing), transform 0.18s var(--easing),
    visibility 0.18s var(--easing);

  li button {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-5);
    width: 100%;
    padding: var(--space-2) var(--space-3);
    background-color: transparent;
    border: 0;
    border-radius: var(--border-radius);
    color: var(--text-secondary);
    font-family: var(--font-sans);
    font-size: var(--fz-md);
    text-align: left;
    white-space: nowrap;
    transition: var(--transition);

    &:hover,
    &:focus-visible {
      background-color: var(--surface-raised);
      color: var(--text);
      outline: none;
    }

    &[aria-selected='true'] {
      color: var(--accent);
    }

    .count {
      color: var(--text-disabled);
      font-size: var(--fz-sm);
    }
  }
`;

const ALL = 'All';

const TagFilter = ({ tags, counts, selected, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useOnClickOutside(wrapperRef, () => setIsOpen(false));

  const options = [ALL, ...tags];

  const choose = tag => {
    onSelect(tag === ALL ? null : tag);
    setIsOpen(false);
  };

  const onKeyDown = e => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <Wrapper ref={wrapperRef} onKeyDown={onKeyDown}>
      <Trigger
        type="button"
        isOpen={isOpen}
        isFiltered={Boolean(selected)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(open => !open)}>
        <span>
          Topic: <span className="value">{selected || ALL}</span>
        </span>
        <span className="chevron" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </Trigger>

      <Panel isOpen={isOpen} role="listbox" aria-label="Filter blogs by topic">
        {options.map(tag => (
          <li key={tag}>
            <button
              type="button"
              role="option"
              aria-selected={tag === (selected || ALL)}
              onClick={() => choose(tag)}>
              <span>{tag === ALL ? 'All topics' : `#${tag}`}</span>
              <span className="count">{tag === ALL ? counts.total : counts.byTag[tag]}</span>
            </button>
          </li>
        ))}
      </Panel>
    </Wrapper>
  );
};

TagFilter.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.string).isRequired,
  counts: PropTypes.shape({ total: PropTypes.number, byTag: PropTypes.object }).isRequired,
  selected: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

export default TagFilter;
